import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { configurationSelectors, State, UserRole, UserType } from '@wam/shared';
import { authenticationSelectors } from '@wam/authentication';
import { map, take } from 'rxjs/operators';
import { ProfileService } from './profile.service';
import { Icon, HelpButtonComponent, BtnComponent } from '@wam/components';
import {
  CodeInputComponent,
  CodeInputModule,
  CodeInputComponentConfigToken,
} from '@kokotree-inc/angular-code-input';
import * as profileActions from '@app/profile/state/profile.actions';
import { LayoutService } from '@app/layouts/layout.service';
import { Tabs } from '@app/shared/tabs.enum';
import { Toggles } from '@app/shared/models/toggles.enum';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatIcon,
    CodeInputModule,
    HelpButtonComponent,
    BtnComponent,
    AsyncPipe,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: CodeInputComponentConfigToken,
      useValue: { codeLength: 4, inputType: 'text', isCharsCode: false },
    },
  ],
})
export class ProfileComponent implements OnInit {
  private store = inject<Store<State>>(Store);

  profileService = inject(ProfileService);

  private layoutService = inject(LayoutService);

  @ViewChild('extendedPinInput') textInput!: ElementRef<HTMLInputElement>;

  maxExtendedPinLength = 8;

  Icon = Icon;
  tabPosition$ = this.store.select(
    configurationSelectors.getUserConfiguration<'top' | 'left'>('tabPosition'),
  );
  language$ = this.store.select(configurationSelectors.getUserConfiguration<string>('language'));
  name$: Observable<string> = this.store.select(authenticationSelectors.getName);
  shouldShowPin$: Observable<boolean> = this.store.pipe(
    select(authenticationSelectors.getCurrentUser),
    map(
      ({ type, roles }) =>
        type === UserType.TEACHER &&
        !(roles.includes(UserRole.SCHOOL_ADMIN) || roles.includes(UserRole.DISTRICT_ADMIN)),
    ),
  );
  teacherPinExtended$: Observable<boolean> = this.store.pipe(
    select(configurationSelectors.isEnabled(Toggles.TEACHER_PIN_NUMBER_EXTEND)),
  );

  toggleLanguage(language: string): void {
    this.profileService.toggleLanguage(language);

    // Re-emit the selected tab to trigger tab translation
    this.layoutService.selectedTab$.next(Tabs.PROFILE);
  }

  toggleTabPosition(tabPosition: 'top' | 'left'): void {
    this.profileService.toggleTabPosition(tabPosition);
  }

  initialPin: string = null;
  currentPin: string = null;
  editingPin: boolean = false;
  extendedPinTouched: boolean = false;
  @ViewChild('code') codeInput: CodeInputComponent;
  pinDisabled = false;

  ngOnInit(): void {
    this.shouldShowPin$.pipe(take(1)).subscribe((shouldShowPin) => {
      if (shouldShowPin) {
        this.loadPin();
      }
    });
  }

  loadPin(): void {
    this.pinDisabled = true;
    this.store
      .select(configurationSelectors.getUserConfiguration<string>('teacherPIN'))
      .pipe(take(1))
      .subscribe((pin: string) => {
        this.currentPin = pin;
        this.initialPin = this.currentPin;
        this.pinDisabled = false;
      });
  }

  savePin(): void {
    this.store.dispatch(profileActions.updateTeacherPIN({ pin: this.currentPin }));
    this.initialPin = this.currentPin;
  }

  cancelPin(): void {
    this.currentPin = this.initialPin;
    this.extendedPinTouched = false;
    this.editingPin = false;
    if (this.codeInput) {
      this.codeInput.reset();
    }
  }

  get pinChanged(): boolean {
    return this.currentPin !== this.initialPin;
  }

  get pinChangedOrEditing(): boolean {
    return this.pinChanged || this.editingPin;
  }

  get extendedPinValid(): boolean {
    return /^\d{4,8}$/.test(this.currentPin);
  }

  onExtendedPinInput(value: string): void {
    this.currentPin = value;
    this.extendedPinTouched = true;
    const input = this.textInput?.nativeElement;
    if (input && this.currentPin.length >= this.maxExtendedPinLength) {
      setTimeout(() => {
        input.setSelectionRange(this.maxExtendedPinLength - 1, this.maxExtendedPinLength - 1);
      }, 0);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const input = this.textInput?.nativeElement;
    if (!input) {
      return;
    }
    if (
      this.currentPin.length >= this.maxExtendedPinLength &&
      (this.isRightArrowAtMaxLength(event) || !this.isNavigationKey(event))
    ) {
      event.preventDefault();
      input.setSelectionRange(this.maxExtendedPinLength - 1, this.maxExtendedPinLength - 1);
    }
  }

  private isNavigationKey(event: KeyboardEvent): boolean {
    const navigationKeys = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Backspace',
      'Delete',
      'Tab',
    ];
    return navigationKeys.includes(event.key);
  }

  private isRightArrowAtMaxLength(event: KeyboardEvent): boolean {
    const cursorPosition = this.textInput?.nativeElement.selectionStart;
    return (
      event.key === 'ArrowRight' &&
      cursorPosition === this.maxExtendedPinLength - 1 &&
      this.currentPin.length === this.maxExtendedPinLength
    );
  }

  codeCompleted(pin: string): void {
    this.currentPin = pin;
    this.editingPin = false;
  }

  codeChanged(): void {
    this.editingPin = true;
  }
}
